import { Injectable, OnDestroy } from '@angular/core';

import { functionMemberListData, IFunctionMemberListData } from '../models/function-member-list-data';
import { IImageDto } from 'src/assets/common/models/image-dto.g';
import { RouteActionParam, routeActionParam } from 'src/app/infrastructure/services/page-services/models/return-page-query-param';
import { _LevelDomainPageService } from 'src/app/infrastructure/services/page-services/_level-domain-page.service';
import { routePage } from 'src/app/infrastructure/services/route-service/models/route-page';
import { IDomainUserData } from '../../domain-user/models/domain-user-data';
import { IDomainUserListData } from '../../domain-user/models/domain-user-list-data';
import { IRouteKeyData } from 'src/app/infrastructure/services/route-service/models/route-key-data';
import { ApplicationUserService } from 'src/app/infrastructure/services/application-user.services/application-user.service';
import { NavigationStackService } from '../../shared/navigation-stack/services/navigation-stack.service';
import { PageData } from 'src/app/infrastructure/services/page-services/models/page-data';
import { TRoutePage } from 'src/app/infrastructure/services/route-service/models/route-page';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { tabName } from 'src/app/infrastructure/services/page-services/models/tab-name';
import { routeName } from 'src/app/infrastructure/services/route-service/models/route-name';

@Injectable({
    providedIn: 'root'
})
export class FunctionMemberPageService
    extends _LevelDomainPageService
{
    private readonly __navigationStackService = injectRequired(NavigationStackService);
    private readonly __applicationUserService = injectRequired(ApplicationUserService);

    photo: IImageDto | null = null;

    constructor() {
        // Geen create-pagina: functies worden alleen via de members-sync-checklists aan- en uitgezet
        // (plan A "FunctionMember-periodes").
        super(null);
    }

    override pageCreate_goto(): void {}

    pageDomain_goto(domainUser: IDomainUserData | IDomainUserListData): void {
        this._pageDomain_goto(domainUser);
    }

    // FunctionMember-pagina in domain-context (alleen data binnen het domain van de bezoeker).
    override pageRoute_goto(item: IFunctionMemberListData, keep?: RouteActionParam, routeParams?: {[key: string]: string}): void {
        // AdminPanel (DomainUser-pagina van een beheerder): er is geen admin-variant van de
        // FunctionMember-pagina — de domain-route zou een Domain "Reflecs" in de drawer opleveren. Open
        // daar de functie zelf (admin-Function-pagina, met o.a. wie de functie vervult).
        if (this._router.url.startsWith(`/${routeName.adminPanel}/`)) {
            const __functionGuid = item[functionMemberListData.function_guid];
            if (__functionGuid) this.page_goto(routePage.adminFunction, {key: __functionGuid}, [keep ?? routeActionParam.none]);
            return;
        }
        const _key = item[functionMemberListData.guid];
        const _dGuid = item[functionMemberListData.requestedDomainUser_guid] ?? this._activatedRouteService?.routeKeys?.dauGuid;
        const _keep = keep ?? routeActionParam.none;
        this.page_goto(routePage.functionMember, {key: _key, dauGuid: _dGuid}, [_keep], routeParams);
    }

    // FunctionMember-pagina in project-context (alleen data binnen het project van de bezoeker) —
    // vanaf de FunctionMembers-tab op een ProjectMember-pagina.
    projectPageRoute_goto(item: IFunctionMemberListData, keep?: RouteActionParam): void {
        const __routeKeys = this._activatedRouteService?.routeKeys;
        const __key = item[functionMemberListData.guid];
        const __keep = keep ?? routeActionParam.none;
        this.page_goto(routePage.projectFunctionMember, {key: __key, dauGuid: __routeKeys?.dauGuid, pamGuid: __routeKeys?.pamGuid}, [__keep]);
    }

    // Link van de FunctionMember-pagina naar de Function-pagina, in dezelfde (domain- of project-)
    // context als waarin de FunctionMember-pagina geopend is.
    functionPage_goto(functionGuid: string, inProject: boolean): void {
        const __routeKeys = this._activatedRouteService?.routeKeys;
        if (inProject) {
            this.page_goto(routePage.projectFunction, {key: functionGuid, dauGuid: __routeKeys?.dauGuid, pamGuid: __routeKeys?.pamGuid}, [routeActionParam.none]);
            return;
        }
        this.page_goto(routePage.function, {key: functionGuid, dauGuid: __routeKeys?.dauGuid}, [routeActionParam.none]);
    }

    // Klik op een functie-rij op de DomainUser-pagina: open de Function-pagina — een project-functie
    // in project-context (via het eigen ProjectMember van de bezoeker in dat project), een
    // domain-functie in domain-context. Zonder eigen ProjectMember in dat project: domain-context.
    // AdminPanel (domain 0): admin-Function-pagina, er is daar geen domain-context.
    functionPageForItem_goto(item: IFunctionMemberListData, keep?: RouteActionParam): void {
        const __functionGuid = item[functionMemberListData.function_guid];
        if (!__functionGuid) return;
        const __keep = keep ?? routeActionParam.none;
        if (this._router.url.startsWith(`/${routeName.adminPanel}/`)) {
            this.page_goto(routePage.adminFunction, {key: __functionGuid}, [__keep]);
            return;
        }
        const __dauGuid = item[functionMemberListData.requestedDomainUser_guid] ?? this._activatedRouteService?.routeKeys?.dauGuid;
        const __pamGuid = item[functionMemberListData.projectMember_guid]
            ? item[functionMemberListData.requestedProjectMember_guid] ?? null
            : null;
        if (__pamGuid) {
            this.page_goto(routePage.projectFunction, {key: __functionGuid, dauGuid: __dauGuid, pamGuid: __pamGuid}, [__keep]);
            return;
        }
        this.page_goto(routePage.function, {key: __functionGuid, dauGuid: __dauGuid}, [__keep]);
    }

    // Klik op een functie-rij in een overzichtslijst (Home, AdminPanel, Domain, Project): open de
    // persoon achter de functie — de ProjectMember-pagina (project-functie) of de DomainUser-pagina
    // (domain-functie) — op de FunctionMembers-tab, vanwaar verder naar de FunctionMember zelf
    // doorgeklikt kan worden. Valt terug op de FunctionMember-pagina als de route-sleutels van de
    // bezoeker voor dat domain/project ontbreken (bv. geen eigen ProjectMember in dat project).
    memberRoute_goto(item: IFunctionMemberListData, keep?: RouteActionParam): void {
        const __routeKeys = this._activatedRouteService?.routeKeys;
        const __dauGuid = item[functionMemberListData.requestedDomainUser_guid] ?? __routeKeys?.dauGuid ?? null;
        const __pmGuid = item[functionMemberListData.projectMember_guid] ?? null;
        const __duGuid = item[functionMemberListData.domainUser_guid] ?? null;
        const __systemDomainGuid = this.__applicationUserService.systemDomainGuid$$();
        const __keep = keep ?? routeActionParam.none;

        // Beheerfunctie van domain 0 (Administrator/SuperAdministrator): de beheerder hoort onder
        // AdminPanel, niet onder een Domain "Reflecs" — de admin-route van de DomainUser-pagina.
        // IsAdmin alleen is niet genoeg: ook ProjectManager/DomainAdministrator in gewone
        // domains hebben IsAdmin, en project-functies hebben ook een DomainUser.
        const __isSystemDomain = !!__systemDomainGuid && item[functionMemberListData.domain_guid] === __systemDomainGuid;
        if (item[functionMemberListData.function_isAdmin] && __isSystemDomain && !__pmGuid && __duGuid && __dauGuid) {
            this.__memberPage_goto(routePage.adminDomainUser, {key: __duGuid, dauGuid: __dauGuid}, __keep);
            return;
        }

        if (__pmGuid) {
            const __pamGuid = item[functionMemberListData.requestedProjectMember_guid] ?? null;
            if (__dauGuid && __pamGuid) {
                this.__memberPage_goto(routePage.projectMember, {key: __pmGuid, dauGuid: __dauGuid, pamGuid: __pamGuid}, __keep);
                return;
            }
        } else if (__duGuid && __dauGuid) {
            this.__memberPage_goto(routePage.domainUser, {key: __duGuid, dauGuid: __dauGuid}, __keep);
            return;
        }

        this.pageRoute_goto(item, keep);
    }

    private __memberPage_goto(page: TRoutePage, routeKeys: IRouteKeyData, keep: RouteActionParam): void {
        const __routePath = PageData.create(page, routeKeys).routePath;
        if (__routePath) this.__navigationStackService.tabName_preset(__routePath, tabName.functionMembers);
        this.page_goto(page, routeKeys, [keep]);
    }

    gotoImage(url: string) {
        this.page_goto(url);
    }

}
